import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type SubmitEvent, useState } from 'react'

import type { HoldRequest } from '@/bindings/HoldRequest'
import type { RestaurantDetail } from '@/bindings/RestaurantDetail'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { Field, Input, Textarea } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card } from '@/components/layout'
import { restaurantKeys, saveHold } from '@/features/restaurants/api'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatDate } from '@/lib/time'

/** Longest note the backend accepts. */
const NOTE_MAX = 500

/**
 * K4: keeps a deactivated restaurant out of the deletion candidates until the date has passed,
 * with a note on who asked. Shown only once the restaurant is deactivated.
 */
export function HoldCard({ restaurant }: { restaurant: RestaurantDetail }) {
  const t = useT()
  const language = useLanguage()
  const queryClient = useQueryClient()
  const [until, setUntil] = useState(restaurant.hold_until ?? '')
  const [note, setNote] = useState(restaurant.hold_note ?? '')
  const [outcome, setOutcome] = useState<'saved' | 'cleared'>()

  const save = useMutation({
    mutationFn: (body: HoldRequest) => saveHold(restaurant.id, body),
    onSuccess: (_, body) => {
      setOutcome(body.hold_until ? 'saved' : 'cleared')
      return queryClient.invalidateQueries({ queryKey: restaurantKeys.all })
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    if (until) save.mutate({ hold_until: until, note: note.trim() || null })
  }

  function clear() {
    setUntil('')
    setNote('')
    save.mutate({ hold_until: null, note: null })
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('restaurants.hold')}</h2>
        {restaurant.hold_until ? (
          <Badge tone="warning">{t('restaurants.heldUntil', { date: formatDate(restaurant.hold_until, language) })}</Badge>
        ) : (
          <Badge tone="muted">{t('restaurants.noHold')}</Badge>
        )}
      </div>
      <p className="text-muted text-sm">{t('restaurants.holdHint')}</p>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
          <Field label={t('restaurants.holdUntil')}>
            <Input
              type="date"
              value={until}
              onChange={(e) => {
                setUntil(e.target.value)
                setOutcome(undefined)
              }}
            />
          </Field>
          <Field label={t('restaurants.holdNote')}>
            <Textarea
              value={note}
              rows={2}
              maxLength={NOTE_MAX}
              onChange={(e) => {
                setNote(e.target.value)
                setOutcome(undefined)
              }}
            />
          </Field>
        </div>
        {outcome && (
          <p role="status" className="text-muted text-sm">
            {t(outcome === 'saved' ? 'restaurants.holdSaved' : 'restaurants.holdCleared')}
          </p>
        )}
        <FormError error={save.error} />
        <div className="flex gap-2">
          <Button type="submit" size="sm" variant="secondary" disabled={!until} loading={save.isPending && save.variables?.hold_until !== null}>
            {t('restaurants.saveHold')}
          </Button>
          {restaurant.hold_until && (
            <Button type="button" size="sm" variant="ghost" loading={save.isPending && save.variables?.hold_until === null} onClick={clear}>
              {t('restaurants.clearHold')}
            </Button>
          )}
        </div>
      </form>
    </Card>
  )
}
